import { callApi } from './apiCall';
import { hasFeatureAccess } from './featurePermission';

const EXPLICIT_OFFSET_DATETIME = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,6})?)?(Z|[+-](\d{2}):?(\d{2}))$/;
const MAX_INTEGER_ID = 2147483647;
const METHODS = new Set(['IN_PRACTICE', 'VIDEO_CALL', 'PHONE']);

const rowsFromPayload = (payload) => {
  if (Array.isArray(payload)) return payload;
  return Array.isArray(payload?.results) ? payload.results : [];
};

const normalizeInteger = (value, max = MAX_INTEGER_ID) => {
  if (typeof value === 'boolean' || value == null || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isInteger(parsed) && parsed >= 1 && parsed <= max ? parsed : null;
};

const normalizeString = (value, maxLength, defaultValue = '') => {
  const normalized = value === undefined ? defaultValue : value;
  return typeof normalized === 'string' && normalized.length <= maxLength ? normalized : null;
};

const isLeapYear = (year) => year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);

const isValidExplicitOffsetDateTime = (value) => {
  if (typeof value !== 'string') return false;
  const match = EXPLICIT_OFFSET_DATETIME.exec(value);
  if (!match) return false;
  const [, yearRaw, monthRaw, dayRaw, hourRaw, minuteRaw, secondRaw = '0', offset, offsetHourRaw, offsetMinuteRaw] = match;
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  const day = Number(dayRaw);
  const hour = Number(hourRaw);
  const minute = Number(minuteRaw);
  const second = Number(secondRaw);
  const daysInMonth = [
    31, isLeapYear(year) ? 29 : 28, 31, 30, 31, 30,
    31, 31, 30, 31, 30, 31,
  ];
  if (year < 1
      || month < 1 || month > 12
      || day < 1 || day > daysInMonth[month - 1]
      || hour > 23 || minute > 59 || second > 59) return false;
  if (offset !== 'Z'
      && (Number(offsetHourRaw) > 23 || Number(offsetMinuteRaw) > 59)) return false;
  return !Number.isNaN(Date.parse(value));
};

const normalizePending = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const practitionerId = normalizeInteger(value.practitionerId);
  const treatmentRecordId = normalizeInteger(value.treatmentRecordId);
  const durationMinutes = normalizeInteger(value.durationMinutes, 1440);
  const reason = normalizeString(value.reason, 255);
  const notes = normalizeString(value.notes, 2000);
  const treatmentName = normalizeString(value.treatmentName, 255);
  const method = value.method === undefined ? 'IN_PRACTICE' : value.method;
  const assignees = value.assignees === undefined ? [] : value.assignees;
  const date = new Date(value.dateTime);
  if (practitionerId === null
      || treatmentRecordId === null
      || durationMinutes === null
      || reason === null
      || notes === null
      || treatmentName === null
      || !isValidExplicitOffsetDateTime(value.dateTime)
      || Number.isNaN(date.getTime())
      || !METHODS.has(method)
      || !Array.isArray(assignees)
      || assignees.length > 50) return null;

  const normalizedAssignees = assignees.map((id) => normalizeInteger(id));
  if (normalizedAssignees.some((id) => id === null)) return null;

  return {
    practitionerId,
    dateTime: value.dateTime,
    durationMinutes,
    reason,
    method,
    assignees: normalizedAssignees,
    notes,
    treatmentName,
    treatmentRecordId,
  };
};

export const resolveDentallyPendingBooking = (payload, { useTasksV2 = false } = {}) => {
  const projected = rowsFromPayload(payload)
    .filter((row) => row?.dentally_pending_booking != null);

  if (projected.length === 0) return { outcome: 'none', pending: null, source: null };
  if (projected.length > 1) return { outcome: 'ambiguous', pending: null, source: null };

  const pending = normalizePending(projected[0].dentally_pending_booking);
  if (!pending || projected[0].id == null || projected[0].id === '') {
    return { outcome: 'malformed', pending: null, source: null };
  }
  return {
    outcome: 'unique',
    pending,
    source: {
      mode: useTasksV2 ? 'v2' : 'v1',
      type: useTasksV2 ? 'task' : 'reminder',
      id: projected[0].id,
    },
  };
};

export const fetchDentallyPendingBooking = async (practiceId, leadId, options = {}) => {
  const useTasksV2 = options.useTasksV2 ?? hasFeatureAccess('tasks-v2', practiceId);
  const endpoint = useTasksV2
    ? `/api/v1/practices/${practiceId}/tasks/?lead=${leadId}&status__in=pending,in_progress&ordering=-created_at&limit=100`
    : `/api/v1/practices/${practiceId}/reminders/?lead=${leadId}&exclude_migrated=true&is_completed=false&ordering=-created_at&limit=100`;
  try {
    const response = await callApi(endpoint);
    if (response?.status !== 200) return { outcome: 'error', pending: null, source: null };
    return resolveDentallyPendingBooking(response.data, { useTasksV2 });
  } catch {
    return { outcome: 'error', pending: null, source: null };
  }
};

export const completeDentallyPendingBooking = async (practiceId, source) => {
  const isTask = source?.mode === 'v2' && source?.type === 'task';
  const isReminder = source?.mode === 'v1' && source?.type === 'reminder';
  if ((!isTask && !isReminder) || source?.id == null || source.id === '') {
    return { ok: false, status: null };
  }
  const response = isTask
    ? await callApi(`/api/v1/practices/${practiceId}/tasks/${source.id}/complete/`, 'post')
    : await callApi(
      `/api/v1/practices/${practiceId}/reminders/${source.id}/`,
      'patch',
      { is_completed: true }
    );
  return {
    ok: response?.status === 200 || response?.status === 204,
    status: response?.status ?? null,
    message: response?.data?.detail || response?.data?.message || null,
  };
};

export const buildPendingBookingFingerprint = (practiceId, leadId, pending) => {
  const start = new Date(pending.dateTime);
  const finish = new Date(start.getTime() + pending.durationMinutes * 60000);
  return {
    practiceId,
    leadId,
    treatmentId: pending.treatmentRecordId,
    startTime: start.toISOString(),
    finishTime: finish.toISOString(),
  };
};
